import type { WriteFileResult } from "@capacitor/filesystem";
import type { FileDeliverParams, FileDeliverType } from "~/api/file-deliver";

import { useCallback } from "react";
import { FileOpener } from "@capacitor-community/file-opener";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { useMutation } from "@tanstack/react-query";
import { saveAs } from "file-saver";

import { useUIContext } from "~/contexts/uiContext";
import { handleApiError } from "~/services/errors";
import { getFileDeliver } from "~/api/file-deliver";

export const useFileDeliver = (props?: { isAnonymous?: boolean }) => {
  const { toast } = useUIContext();
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: ({ type, params }: { type: FileDeliverType; params?: FileDeliverParams }) =>
      getFileDeliver({ type, params, isAnonymous: props?.isAnonymous || false }),
    onError(error) {
      handleApiError(error, toast);
    }
  });

  const openFile = useCallback(
    async ({
      itemType,
      itemParams,
      fileType,
      newTab = true
    }: {
      itemType: FileDeliverType;
      itemParams?: FileDeliverParams;
      fileType?: string | null | undefined;
      newTab?: boolean;
    }) => {
      const { file, filename, filetype } = await mutateAsync({
        type: itemType,
        params: itemParams
      });
      if (!file) return;

      const type = fileType || filetype || "application/pdf";
      const blob = new Blob([file], { type });

      const fileName = filename || "fichier-hexa-coop";

      // If Native
      if (Capacitor.isNativePlatform()) {
        await downloadNativeFile({
          blob,
          filename: fileName,
          callback: async (savedFile) => {
            if (type === "application/pdf") {
              // Open the file
              const filePath = savedFile.uri;
              await FileOpener.open({ filePath, contentType: type });
            }
          }
        });
        toast("Fichier téléchargé avec succès", "success");
        return;
      }

      // If Web
      if (type !== "application/pdf") {
        saveAs(file, fileName);
        return;
      }

      const url = URL.createObjectURL(blob);
      if (newTab) {
        const newWindow = window.open("/", "_blank");
        if (!newWindow) return;
        newWindow.document.title = fileName;
        newWindow.onload = () => {
          newWindow.location = url;
        };
        return;
      }
      window.open(url, "_self");
      window.document.title = fileName;
    },
    [mutateAsync, toast]
  );

  const downloadFile = useCallback(
    async ({
      itemType,
      itemParams,
      fileType: fileTypeProp
    }: {
      itemType: FileDeliverType;
      itemParams: FileDeliverParams;
      fileType?: string | null | undefined;
    }) => {
      const { file, filename, filetype } = await mutateAsync({
        type: itemType,
        params: itemParams
      });
      if (!file) return;

      const fileName = filename || "fichier-hexa-coop";
      const fileType = fileTypeProp || filetype || "application/pdf";
      const blob = new Blob([file], { type: fileType });

      // If Native
      if (Capacitor.isNativePlatform()) {
        await downloadNativeFile({
          blob,
          filename: fileName,
          callback: async (savedFile) => {
            window.open(savedFile.uri, "_blank");
          }
        });
        toast("Fichier téléchargé avec succès", "success");
      } else {
        // If Web
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      }
    },
    [mutateAsync, toast]
  );

  return { openFile, downloadFile, isLoading };
};

const downloadNativeFile = async ({
  blob,
  filename,
  callback
}: {
  blob: Blob;
  filename: string;
  callback: (savedFile: WriteFileResult) => void;
}) => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);

  reader.onloadend = async () => {
    const base64Data = reader.result?.toString().split(",")[1]; // Extract Base64 content

    if (!base64Data) throw new Error("Failed to convert blob to Base64");

    // Save file
    const savedFile = await Filesystem.writeFile({
      path: filename || "fichier-hexa-coop",
      data: base64Data,
      directory: Directory.Documents,
      recursive: true
    });

    callback(savedFile);
  };
};
